"use client";
import Image from "next/image";
import { useEffect } from "react";

import { useModalStore } from "@/store/modal.store";
import { useSliderStore } from "@/store/slider.store";
import { CustomSlider } from "@/ui/Common/CustomSlider";
import { cn } from "@/utils/cn";

import iconEmpty from "public/assets/img/empty.png";

import { IImagePopulated } from "@/types/images.types";
import ResponsiveImage from "../../ResponsiveImage";
import styles from "./style.module.scss";

// Gallery image component with fallback support
const GalleryImage = ({
  image,
  index,
  title,
}: {
  image: IImagePopulated;
  index: number;
  title: string;
}) => {
  // Check if we have a valid image URL
  const hasValidImageUrl = image && image.length > 0;

  // If no valid URL or fallback failed, show empty image
  if (!hasValidImageUrl) {
    return (
      <Image
        src={iconEmpty}
        alt={title || `Image ${index + 1}`}
        fill={true}
        style={{ objectFit: "contain" }}
        sizes="100dvw"
        priority={index === 0}
      />
    );
  }

  return (
    <div className="w-full h-full">
      <ResponsiveImage
        images={image}
        alt={title || `Image ${index + 1}`}
        fill={true}
        objectFit="contain"
        sizes="100dvw"
        priority={index === 0}
      />
    </div>
  );
};

// TODO: Move to widgets/Modal/Gallery

export const Gallery = () => {
  const { modal, closeModal } = useModalStore();
  const { slides, currentSlide, setCurrentSlide } = useSliderStore();

  // Add the listener to Popstate to close the modal window
  useEffect(() => {
    const handlePopState = () => {
      closeModal();
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [closeModal]);

  return (
    <div className={styles.wrapper}>
      <CustomSlider
        className={styles.gallerySliderMode}
        showCounter={true}
        navigationPosition="center"
        startIndex={currentSlide}
        setCurrentSlide={setCurrentSlide}
        isModal={true}
        loop={true}
        main={
          <div className={cn(styles.sliderContainer)}>
            {slides?.length > 0 ? (
              slides?.map((image, index) => {
                return (
                  <div
                    key={index}
                    className={styles.slideItem}
                  >
                    <GalleryImage
                      image={image.populated}
                      index={index}
                      title={modal.modalName || `Image ${index + 1}`}
                    />
                  </div>
                );
              })
            ) : (
              <div className={styles.slideItem}>
                <Image
                  width={284}
                  height={158}
                  src={iconEmpty}
                  alt={modal.modalName || "Empty gallery"}
                  style={{ objectFit: "contain" }}
                />
              </div>
            )}
          </div>
        }
        length={slides?.length}
      />
    </div>
  );
};
